// noinspection TypeScriptUnresolvedVariable,TypeScriptValidateTypes

import type {
  BooleanEntityCustomFormField,
  CustomFieldInput,
  DateTimeEntityCustomFormField,
  EntityCustomFormField,
  FloatEntityCustomFormField,
  FloatInputField,
  FloatPossibleValue,
  FloatRadioField,
  FloatSelectField,
  IntEntityCustomFormField,
  IntInputField,
  IntPossibleValue,
  IntRadioField,
  IntSelectField,
  ListFloatEntityCustomFormField,
  ListIntEntityCustomFormField,
  ListStringEntityCustomFormField,
  LongEntityCustomFormField,
  RelativeDate,
  StringEntityCustomFormField,
  StringInputField,
  StringPossibleValue,
  StringRadioField,
  StringSelectField
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  CustomFieldValueType,
  DateTimeCustomFormFieldType,
  DateUnit,
  FloatCustomFormFieldType,
  FormInputType,
  IntCustomFormFieldType,
  StringCustomFormFieldType
} from '@aurora/shared-generated/types/graphql-schema-types';
import { FormInputFieldInputType } from '../../components/form/enums';
import type { PossibleValueTypes } from '../../components/form/types';

const CUSTOM_FEATURE_PREFIX = 'c_';

export interface DateTimeValue {
  dateTime: Date;
}

export function getRelativeDate(
  relativeDate: RelativeDate,
  forward: boolean,
  fromDate: Date
): Date {
  const { unit, duration } = relativeDate;
  switch (unit) {
    case DateUnit.Day: {
      return new Date(
        fromDate.setDate(forward ? fromDate.getDate() + duration : fromDate.getDate() - duration)
      );
    }
    case DateUnit.Month: {
      return new Date(
        fromDate.setMonth(forward ? fromDate.getMonth() + duration : fromDate.getMonth() - duration)
      );
    }
    case DateUnit.Year: {
      return new Date(
        fromDate.setFullYear(
          forward ? fromDate.getFullYear() + duration : fromDate.getFullYear() - duration
        )
      );
    }
  }
}

export function getFormFieldInputType(inputType: FormInputType): FormInputFieldInputType {
  if (inputType === FormInputType.Text) {
    return FormInputFieldInputType.TEXT;
  } else if (inputType === FormInputType.Hidden) {
    return FormInputFieldInputType.HIDDEN;
  }
  return FormInputFieldInputType.TEXT;
}

function getDefaultValueForCustomField(
  field: EntityCustomFormField
): string | DateTimeValue | boolean | string[] {
  const { valueType: formFieldType } = field;
  const customFormFieldType: CustomFieldValueType = formFieldType as CustomFieldValueType;
  switch (customFormFieldType) {
    case CustomFieldValueType.Boolean: {
      const { booleanDefaultValue, booleanSetting } = field as BooleanEntityCustomFormField;
      return booleanSetting?.value ?? booleanDefaultValue ?? false;
    }
    case CustomFieldValueType.Datetime: {
      const { dateTimeDefaultValue, dateTimeField, dateTimeSetting } =
        field as DateTimeEntityCustomFormField;
      const dateString = dateTimeSetting?.value ?? dateTimeDefaultValue;
      const { dateTimeType } = dateTimeField;
      switch (dateTimeType) {
        case DateTimeCustomFormFieldType.Input: {
          return `${dateString ?? ''}`;
        }
        default: {
          return dateString
            ? ({ dateTime: new Date(dateString as string) } as unknown as DateTimeValue)
            : ({ dateTime: new Date() } as unknown as DateTimeValue);
        }
      }
    }
    case CustomFieldValueType.Float: {
      const { floatDefaultValue, floatSetting } = field as FloatEntityCustomFormField;
      return `${floatSetting?.value ?? floatDefaultValue ?? ''}`;
    }
    case CustomFieldValueType.Int: {
      const { intDefaultValue, intSetting } = field as IntEntityCustomFormField;
      return `${intSetting?.value ?? intDefaultValue ?? ''}`;
    }
    case CustomFieldValueType.Long: {
      const { longDefaultValue, longSetting } = field as LongEntityCustomFormField;
      return `${longSetting?.value ?? longDefaultValue ?? ''}`;
    }
    case CustomFieldValueType.String: {
      const { stringDefaultValue, stringSetting } = field as StringEntityCustomFormField;
      return stringSetting?.value ?? stringDefaultValue ?? '';
    }
    case CustomFieldValueType.ListString: {
      const { stringListDefaultValue, stringListSetting } =
        field as ListStringEntityCustomFormField;
      return stringListSetting?.value ?? stringListDefaultValue ?? [];
    }
    case CustomFieldValueType.ListFloat: {
      const { floatListDefaultValue, floatListSetting } = field as ListFloatEntityCustomFormField;
      return (
        floatListSetting?.value.map(value => `${value}`) ??
        floatListDefaultValue?.map(value => `${value}`) ??
        []
      );
    }
    case CustomFieldValueType.ListInt: {
      const { intListDefaultValue, intListSetting } = field as ListIntEntityCustomFormField;
      return (
        intListSetting?.value.map(value => `${value}`) ??
        intListDefaultValue?.map(value => `${value}`) ??
        []
      );
    }
  }
  return '';
}

function addDefaultValueForFeatureField(
  customName: string,
  defaultValues: Record<string, unknown>,
  field: EntityCustomFormField
) {
  // split the custom field name to get the custom feature name and custom name
  const [customFeatureName, customFieldNameFirstPart, ...additional] = customName.split('.');
  const customFieldName = [customFieldNameFirstPart, ...additional].join('.');
  if (!defaultValues[customFeatureName]) {
    defaultValues[customFeatureName] = {};
  }
  defaultValues[customFeatureName][customFieldName] = getDefaultValueForCustomField(field);
}

function getDefaultValuesForCustomFields(
  customFormFields: Array<EntityCustomFormField>
): Record<string, unknown> {
  const defaultValues: Record<string, unknown> = {};
  customFormFields.forEach(field => {
    const { name: customName } = field;
    if (customName.startsWith(CUSTOM_FEATURE_PREFIX)) {
      addDefaultValueForFeatureField(customName, defaultValues, field);
    } else {
      defaultValues[customName] = getDefaultValueForCustomField(field);
    }
  });
  return defaultValues;
}

/**
 * Checks if the provided value conforms to the types defined by `PossibleValueTypes`.
 * @param value The value for which the type is to be checked.
 * @returns Returns `true` if the type of the value conforms to the type of `PossibleValueTypes`, otherwise returns `false`.
 */
function isPossibleValueTypes(
  value: StringPossibleValue | IntPossibleValue | FloatPossibleValue
): value is PossibleValueTypes {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__typename' in value &&
    (value.__typename === 'StringPossibleValue' ||
      value.__typename === 'FloatPossibleValue' ||
      value.__typename === 'IntPossibleValue')
  );
}

/**
 * Checks if all elements in the provided array conform to the types defined by `PossibleValueTypes`.
 * @param possibleValues An array of values to be checked.
 * @returns Returns `true` if all elements in the array conform to the type of `PossibleValueTypes`, otherwise returns `false`.
 */
function isArrayOfPossibleValueType(
  possibleValues: (StringPossibleValue | IntPossibleValue | FloatPossibleValue)[]
): boolean {
  return (
    Array.isArray(possibleValues) && possibleValues.every(value => isPossibleValueTypes(value))
  );
}

/**
 * @param customField the entity custom form field.
 * @returns Return the possible values array for a custom field or null.
 */

function getPossibleValuesForCustomField(
  customField: EntityCustomFormField
): PossibleValueTypes[] | null {
  const { valueType: formFieldType } = customField;
  const customFormFieldType: CustomFieldValueType = formFieldType as CustomFieldValueType;
  switch (customFormFieldType) {
    case CustomFieldValueType.String: {
      const { stringField } = customField as StringEntityCustomFormField;
      const { stringType } = stringField;
      const formFieldControlType: StringCustomFormFieldType =
        stringType as StringCustomFormFieldType;

      switch (formFieldControlType) {
        case StringCustomFormFieldType.Select: {
          const { stringSelectValues } = stringField as StringSelectField;
          return stringSelectValues;
        }
        case StringCustomFormFieldType.Radio: {
          const { stringRadioValues } = stringField as StringRadioField;
          return stringRadioValues;
        }
        case StringCustomFormFieldType.Input: {
          const { stringInputValues } = stringField as StringInputField;
          return stringInputValues;
        }
      }
      break;
    }

    case CustomFieldValueType.Int: {
      const { intField } = customField as IntEntityCustomFormField;
      const { intType } = intField;
      const formFieldControlType: IntCustomFormFieldType = intType as IntCustomFormFieldType;

      switch (formFieldControlType) {
        case IntCustomFormFieldType.Select: {
          const { intSelectValues } = intField as IntSelectField;
          return intSelectValues;
        }
        case IntCustomFormFieldType.Radio: {
          const { intRadioValues } = intField as IntRadioField;
          return intRadioValues;
        }

        case IntCustomFormFieldType.Input: {
          const { intInputValues } = intField as IntInputField;
          return intInputValues;
        }
      }
      break;
    }

    case CustomFieldValueType.Float: {
      const { floatField } = customField as FloatEntityCustomFormField;
      const { floatType } = floatField;
      const formFieldControlType: FloatCustomFormFieldType = floatType as FloatCustomFormFieldType;

      switch (formFieldControlType) {
        case FloatCustomFormFieldType.Select: {
          const { floatSelectValues } = floatField as FloatSelectField;
          return floatSelectValues;
        }
        case FloatCustomFormFieldType.Radio: {
          const { floatRadioValues } = floatField as FloatRadioField;
          return floatRadioValues;
        }
        case FloatCustomFormFieldType.Input: {
          const { floatInputValues } = floatField as FloatInputField;
          return floatInputValues;
        }
      }
    }
  }
  return null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDateTimeValue(value: unknown): value is DateTimeValue {
  return typeof isObject(value) && value['dateTime'] && value['dateTime'] instanceof Date;
}

function isCustomFieldGroup(value: unknown): value is Record<string, unknown> {
  return isObject(value) && !isDateTimeValue(value);
}

function convertCustomFieldValue(customFieldValue: unknown): string {
  if (isDateTimeValue(customFieldValue)) {
    const { dateTime } = customFieldValue;
    return convertCustomFieldValue((dateTime as Date).getTime());
  }
  if (Array.isArray(customFieldValue)) {
    return customFieldValue.map(item => item.value ?? item).join(', ');
  }
  return customFieldValue as string;
}

/**
 * Function to convert custom fields to custom field inputs filtering out the unchanged custom fields
 *
 *@param customFields the custom fields to be converted
 *@param customFormFields the custom form fields to get the default values to compare with,
 * if not provided, no custom field will be filtered out
 */
export function getCustomFieldInputs(
  customFields: object,
  customFormFields?: Array<EntityCustomFormField>
): Array<CustomFieldInput> {
  const customFieldInputs: Array<CustomFieldInput> = [];
  const defaultValues = customFormFields ? getDefaultValuesForCustomFields(customFormFields) : {};
  Object.keys(customFields).forEach(customFieldName => {
    const customField = customFields[customFieldName];
    // custom fields from a custom feature plugin will have a prefix of 'c_' and will be an object
    if (customFieldName.startsWith(CUSTOM_FEATURE_PREFIX) && isCustomFieldGroup(customField)) {
      const customFieldGroupInputs = getCustomFieldInputs(customField, customFormFields);
      for (const customFieldGroupInput of customFieldGroupInputs) {
        const { name, value } = customFieldGroupInput;
        customFieldInputs.push({
          name: `${customFieldName}.${name}`,
          value: value
        });
      }
    } else {
      const customFieldValue = customField ? convertCustomFieldValue(customField) : customField;
      if (customFieldValue != defaultValues[customFieldName]) {
        customFieldInputs.push({
          name: customFieldName,
          value: customFieldValue
        });
      }
    }
  });
  return customFieldInputs;
}

/**
 * @param customField the entity custom form field
 * @returns Returns true if the custom field possesses attributes of type PossibleValueTypes, otherwise returns false.
 */
function isCustomFieldPossibleValueType(customField: EntityCustomFormField): boolean {
  const possibleValues = getPossibleValuesForCustomField(customField);
  return isArrayOfPossibleValueType(possibleValues);
}

export {
  getDefaultValuesForCustomFields,
  getDefaultValueForCustomField,
  isCustomFieldPossibleValueType,
  getPossibleValuesForCustomField,
  isPossibleValueTypes,
  isArrayOfPossibleValueType
};
